import {inject, Pipe, PipeTransform} from '@angular/core';
import moment, {MomentInput} from 'moment';
import {DateFormatKey, isDateFormatKey} from '../date-format/date-format';
import {DateFormatService} from '../date-format/date-format.service';

/** The keys that carry the full date — the only ones "Today" stands in for. */
const TODAY_LABEL_KEYS: ReadonlySet<DateFormatKey> = new Set(['date', 'dateTime']);

/**
 * Formats a date for display — a tile's, or any template's. The format is either a {@link DateFormatKey}, which
 * follows the selected company's default/US profile (see `DateFormatService`), or raw **moment**
 * tokens (`YY.MM.DD HH:mm`), used verbatim in every profile. Raw tokens are not the date-fns ones
 * the same pipe uses in the client-web repo — a format copied from there has to be translated.
 *
 * With `showTodayLabel`, a date falling on today renders as "Today" (plus the time, when the
 * format carries one) instead of the date part. Only formats with the full date qualify: a key in
 * {@link TODAY_LABEL_KEYS}, or raw tokens containing `YY.MM.DD`.
 */
@Pipe({
  name: 'formatDate'
})
export class FormatDatePipe implements PipeTransform {
  private readonly formats = inject(DateFormatService);
  private readonly rawDateTokens = 'YY.MM.DD';
  private readonly rawTimeTokens = 'HH:mm';
  private readonly todayLabel = $localize`Today`;

  transform(date: MomentInput, dateFormat: DateFormatKey | string = 'dateTime', showTodayLabel = false): string {
    const parsed = moment(date);
    if (!parsed.isValid()) {
      return '';
    }

    const key = isDateFormatKey(dateFormat) ? dateFormat : null;
    if (showTodayLabel && parsed.isSame(moment(), 'day')) {
      if (key && TODAY_LABEL_KEYS.has(key)) {
        return key === 'dateTime' ? `${this.todayLabel} ${parsed.format(this.formats.pattern('time'))}` : this.todayLabel;
      }
      if (!key && dateFormat.includes(this.rawDateTokens)) {
        return dateFormat.includes(this.rawTimeTokens)
          ? `${this.todayLabel} ${parsed.format(this.rawTimeTokens)}`
          : this.todayLabel;
      }
    }

    return parsed.format(key ? this.formats.pattern(key) : dateFormat);
  }
}
